// Single source of truth for programmatic event-type landing pages.
// Targets high-volume search queries that the discipline pages (ROAD/GRAVEL/MTB)
// don't cover: "radmarathon <region>", "jedermann radrennen <region>",
// "brevet <region>", "ctf <ort>". Each entry drives a route, the Landing page
// labels, the footer links and (mirrored in Kotlin) the sitemap + server render.
export interface EventTypeLanding {
  /** URL slug, e.g. 'radmarathon' → /radmarathon and /radmarathon/:bundesland */
  slug: string;
  /**
   * EventType-Enums, die diese Seite einsammelt — bewusst mehrere.
   * Die Eventart-Taxonomie wurde auf die radsport-events-Unterkategorien erweitert, dabei kamen
   * feinere Typen dazu (RADMARATHON, GRAVEL_BREVET), waehrend Bestandsevents und Alt-Importe noch
   * auf den groberen Legacy-Typen (MARATHON, BREVET) liegen. Eine Landing, die nur einen davon
   * filtert, wuerde die jeweils andere Haelfte unsichtbar machen — bei /radmarathon exakt die
   * frisch importierten Events.
   */
  eventTypes: string[];
  /** Plural display label for title/H1, e.g. 'Radmarathons' */
  label: string;
  /** Noun phrase for the intro sentence */
  introNoun: string;
}

export const EVENT_TYPE_LANDINGS: EventTypeLanding[] = [
  // MTB_MARATHON bleibt bewusst draussen: andere Suchintention ("MTB-Marathon"), andere Disziplin.
  { slug: 'radmarathon', eventTypes: ['MARATHON', 'RADMARATHON'], label: 'Radmarathons', introNoun: 'Radmarathons und Marathon-Strecken' },
  { slug: 'jedermann-rennen', eventTypes: ['JEDERMANN'], label: 'Jedermann-Rennen', introNoun: 'Jedermann-Radrennen' },
  { slug: 'brevet', eventTypes: ['BREVET', 'GRAVEL_BREVET'], label: 'Brevets', introNoun: 'Brevets und Randonnées' },
  { slug: 'ctf', eventTypes: ['CTF'], label: 'CTF', introNoun: 'CTF (Country-Tourenfahrten)' },
  // Ultra hat keine eigene Kategorie (es ist ein Format, kein Untergrund) und steht deshalb als
  // Eventart unter Rennrad, Gravel und MTB. Diese Seite ist der einzige Ort, an dem alle Ultras
  // kategorieuebergreifend zusammenkommen — so wie /brevet Rennrad- und Gravel-Brevets buendelt.
  { slug: 'ultracycling', eventTypes: ['ULTRA'], label: 'Ultra-Rennen', introNoun: 'Ultra-Rennen und Ultracycling-Events' },
  // Bikepacking bekommt eine eigene Seite statt eines Abschnitts auf /ultracycling: der Begriff
  // wird eigenstaendig gesucht, und die Suchintention ist eine andere (Ausruestung, Route,
  // Selbstversorgung statt Zielzeit). Wie /ultracycling sammelt sie quer ueber Rennrad, Gravel
  // und MTB ein.
  { slug: 'bikepacking', eventTypes: ['BIKEPACKING'], label: 'Bikepacking', introNoun: 'Bikepacking-Events und self-supported Rennen' },
  // Die alte Adresse /termine-triathlon bringt weiter 56.835 Einblendungen und 2.250 Klicks in
  // drei Monaten — nach der Startseite die größte Einzelquelle der Website. Bis zum 17.09.2026
  // landeten alle diese Besucher auf der Startseite und fanden dort nichts zum Thema. Diese Seite
  // ist ihr Ziel. Sie ist dünn (7 Triathlon- und Duathlon-Events im Bestand) und trägt nur, wenn
  // die Redaktion die Termine pflegt; eine dünne Seite ist trotzdem mehr als eine falsche.
  // TRIATHLON_CROSS steht mit drin wie MARATHON bei /radmarathon: heute leer, aber dieselbe Frage.
  { slug: 'triathlon', eventTypes: ['TRIATHLON', 'TRIATHLON_CROSS'], label: 'Triathlon', introNoun: 'Triathlon-Termine' },
];

export const findEventTypeLandingByTypes = (eventTypes?: string[]): EventTypeLanding | undefined =>
  eventTypes?.length
    ? EVENT_TYPE_LANDINGS.find(l => l.eventTypes.some(t => eventTypes.includes(t)))
    : undefined;

/**
 * Permanente Events ohne festen Termin, ganzjährig oder in ihrer Saison.
 * Keine Eventart-Landing, sondern ein Merkmal am Event (`permanent`) — deshalb eigener
 * Eintrag statt eines Elements in `EVENT_TYPE_LANDINGS`. Ohne Jahr im Titel: die Seite veraltet
 * nicht. Gespiegelt in `SeoPageController` und `SitemapController`; der Bot-Pfad braucht den Slug
 * zusätzlich in `frontend/nginx.conf.template`. Siehe docs/bereiche/permanente-events.md.
 */
export const PERMANENT_LANDING = {
  slug: 'permanente-events',
  /** Name im Umschalter über der Liste. */
  label: 'Permanent',
  /** Überschrift über der Liste. */
  heading: 'Permanente Events',
  title: 'Permanente Events: Radsport ohne festen Termin | Radsport Events',
  description: 'Permanente Radsport-Events ohne festen Termin für Rennrad, Gravel und MTB: ganzjährig oder in ihrer Saison fahrbar, mit Ort, Strecken und Anmeldelink.',
  intro: 'Events ohne festen Termin, die du auf eigene Faust fährst, ganzjährig oder in ihrer Saison. Mit Ort, Strecken und Anmeldelink.',
} as const;
